import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Smartphone, CheckCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScannerAuthorize() {
  const { qrData } = useParams();
  const navigate = useNavigate();
  const [authorizing, setAuthorizing] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);

  useEffect(() => {
    // Auto-authorize on component mount
    handleAuthorize();
  }, [qrData]);

  const handleAuthorize = async () => {
    setAuthorizing(true);
    try {
      const response = await axios.post(`${API_URL}/scanner/authorize`, {
        qr_data: decodeURIComponent(qrData),
        user_agent: navigator.userAgent,
        ip_address: 'client' // Will be captured on server
      });

      // Save token to localStorage
      const parts = decodeURIComponent(qrData).split('|');
      if (parts.length === 2) {
        const [eventId, deviceToken] = parts;
        localStorage.setItem(`scanner_token_${eventId}`, deviceToken);
        setEventInfo({ event_id: eventId, device_token: deviceToken });
      }

      setAuthorized(true);
      toast.success('Device autorizzato con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autorizzazione');
      setAuthorizing(false);
    }
  };

  const goToScanner = () => {
    if (eventInfo) {
      navigate(`/scanner?event_id=${eventInfo.event_id}`);
    } else {
      navigate('/scanner');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              {authorized ? '✅ Autorizzazione Completata' : '🔐 Autorizzazione Scanner'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {authorizing && !authorized ? (
              <div className="text-center py-12">
                <Smartphone className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-pulse" />
                <p className="text-lg text-gray-900 font-medium">Autorizzazione in corso...</p>
                <p className="text-sm text-gray-600 mt-2">Attendere prego</p>
              </div>
            ) : authorized ? (
              <div className="text-center py-8">
                <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Device Autorizzato!
                </h3>
                <p className="text-gray-600 mb-6">
                  Questo dispositivo è ora autorizzato a scansionare biglietti per l'evento.
                </p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-green-900">
                    <strong>✓ Token salvato</strong><br/>
                    Puoi ora utilizzare l'app Scanner
                  </p>
                </div>

                <Button onClick={goToScanner} className="w-full" size="lg">
                  Vai allo Scanner
                </Button>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">Autorizzazione non riuscita</p>
                <Button onClick={handleAuthorize} className="mt-4">
                  Riprova
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
